import styles from './ContactFormCard.module.css';
import useInView from '../Animation/useInView.ts';
import { StatementCommand, CommunicationBy } from '../../services/api/types';
import { submitStatement } from '../../services/api/methods.ts';
import { useState, useCallback } from 'react';

const COMMUNICATION_OPTIONS = [
  { value: 'Telegram' as const, label: 'Telegram' },
  { value: 'Whatsapp' as const, label: 'WhatsApp' },
  { value: 'PhoneConversation' as const, label: 'По телефону' },
];

interface SplitContactCardProps {
  image?: string;
  background?: string;
}

const SplitContactCard = ({ image = '/images/contact-bg.jpg', background = '#ffffff' }: SplitContactCardProps) => {
  const [formData, setFormData] = useState<{
    phone: string;
    message: string;
    contactMethod: CommunicationBy;
  }>({
    phone: '',
    message: '',
    contactMethod: 'Telegram',
  });

  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleSelect = useCallback((value: CommunicationBy) => {
    setFormData((prev) => ({ ...prev, contactMethod: value }));
    setIsOpen(false);
  }, []);

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!formData.phone.trim()) newErrors.phone = 'Телефон обязателен';
    if (!formData.message.trim()) newErrors.message = 'Сообщение не может быть пустым';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const command: StatementCommand = {
        Phone: formData.phone.trim(),
        Message: formData.message.trim(),
        ConvenientHimContact: formData.contactMethod,
      };

      await submitStatement(command);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Ошибка API:', error);
      setErrors({ phone: 'Ошибка соединения. Попробуйте позже.' });
    } finally {
      setIsLoading(false);
    }
  }, [formData, validate]);

  return (
    <div
      id='ContactFormCardId'
      ref={ref}
      className={`${styles.card} ${isInView ? styles.visible : ''}`}
      style={{ '--card-bg': background } as React.CSSProperties}
    >
      <div className={styles.imageSide} style={{ backgroundImage: `url(${image})` }} />

      <div className={styles.formSide}>
        <div className={styles.formContainer}>
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>Удобный способ связи</label>
                <div className={styles.customSelect}>
                  <div
                    className={`${styles.selected} ${isOpen ? styles.selectedOpen : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
                  >
                    {COMMUNICATION_OPTIONS.find((opt) => opt.value === formData.contactMethod)?.label}
                    <span className={`${styles.arrow} ${isOpen ? styles.arrowUp : styles.arrowDown}`}></span>
                  </div>
                  {isOpen && (
                    <ul className={styles.options} role="listbox">
                      {COMMUNICATION_OPTIONS.map((option) => (
                        <li
                          key={option.value}
                          onClick={() => handleSelect(option.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSelect(option.value)}
                          className={styles.option}
                          role="option"
                          tabIndex={0}
                        >
                          {option.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="phone" className={styles.label}>
                  Телефон
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+7 (999) 123-45-67"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                  maxLength={15}
                  disabled={isLoading}
                />
                {errors.phone && <span className={styles.errorMessage}>{errors.phone}</span>}
              </div>

              <div className={styles.field}>
                <label htmlFor="message" className={styles.label}>
                  Сообщение
                </label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Чем можем помочь?"
                  maxLength={995}
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className={`${styles.textarea} ${errors.message ? styles.textareaError : ''}`}
                  disabled={isLoading}
                />
                {errors.message && <span className={styles.errorMessage}>{errors.message}</span>}
              </div>

              <button
                type="submit"
                className={`${styles.button} ${isLoading ? styles.buttonLoading : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className={styles.spinner}></span>
                    Отправляем...
                  </>
                ) : (
                  'Отправить заявку'
                )}
              </button>
            </form>
          ) : (
            <div className={styles.success}>
              <div className={styles.checkmarkContainer}>
                <svg className={styles.checkmark} viewBox="0 0 52 52">
                  <circle
                    className={styles.checkmarkCircle}
                    cx="26"
                    cy="26"
                    r="25"
                    fill="none"
                  />
                  <path
                    className={styles.checkmarkPath}
                    fill="none"
                    d="M14.1 27.2l7.1 7.2 16.7-18.8"
                  />
                </svg>
              </div>
              <h3 className={styles.successTitle}>Спасибо!</h3>
              <p className={styles.successText}>Ваша заявка отправлена. Свяжемся в ближайшее время.</p>
              <button
                className={styles.successButton}
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({ phone: '', message: '', contactMethod: 'Telegram' });
                  setErrors({});
                }}
              >
                Новая заявка
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SplitContactCard;